import {SpørreskjemaSpørsmålRedigerKomponent as Spørsmål, SpørsmålSkjema} from '../spørreskjema-spørsmål-rediger/spørreskjema-spørsmål-rediger';

/**
 * Regler for spørsmål som benytter samme svaralternativer som et annet spørsmål (alternativkilde) i et spørreskjema.
 *
 * Koblingen mellom spørsmål og kilde finnes kun på klienten - ved lagring sendes alternativene til kilden for hvert spørsmål.
 * En kilde må være av samme type som spørsmålet, og må ha egne alternativer (dvs ikke selv ha en kilde), slik at det aldri blir kjeder.
 */
export class Alternativkilder {

    /** Spørsmål som kan velges som alternativkilde for angitt spørsmål: øvrige spørsmål av samme type med egne alternativer */
    static kandidaterFor(spørsmål: SpørsmålSkjema, alle: SpørsmålSkjema[]): SpørsmålSkjema[] {
        if (!Spørsmål.harAlternativer(spørsmål.controls.type.value)) return [];
        return alle.filter(kandidat => Alternativkilder.kanVæreKildeFor(kandidat, spørsmål));
    }

    /**
     * Fjerner koblinger til kilder som ikke lenger er gyldige: kilden er fjernet fra skjemaet, har fått en annen type eller
     * har selv fått en kilde. Spørsmålet får da en kopi av alternativene til kilden, slik at ingen alternativer går tapt.
     */
    static normaliser(alle: SpørsmålSkjema[]): void {
        alle.forEach(spørsmål => {
            const kilde = Spørsmål.kildeFor(spørsmål);
            if (kilde && !(alle.includes(kilde) && Alternativkilder.kanVæreKildeFor(kilde, spørsmål)))
                Spørsmål.kopierAlternativerFraKilde(spørsmål, kilde);
        });
    }

    /**
     * Kobler spørsmål som har helt like alternativer (tittel, verdi og farge i samme rekkefølge) som et tidligere spørsmål
     * av samme type til det første av disse. Benyttes ved åpning av eksisterende skjema, da koblingene ikke lagres på serveren.
     */
    static koblIdentiske(alle: SpørsmålSkjema[]): void {
        alle.forEach((spørsmål, indeks) => {
            if (!Spørsmål.harAlternativer(spørsmål.controls.type.value) || spørsmål.controls.alternativer.length === 0) return;
            const kilde = alle.slice(0, indeks).find(tidligere =>
              Alternativkilder.kanVæreKildeFor(tidligere, spørsmål) && Alternativkilder.harLikeAlternativer(tidligere, spørsmål));
            if (kilde) spørsmål.controls.alternativkilde.setValue(kilde, {emitEvent: false});
        });
    }

    // private hjelpemetoder

    private static kanVæreKildeFor(kandidat: SpørsmålSkjema, spørsmål: SpørsmålSkjema): boolean {
        return kandidat !== spørsmål
          && kandidat.controls.type.value === spørsmål.controls.type.value
          && Spørsmål.harAlternativer(kandidat.controls.type.value)
          && Spørsmål.kildeFor(kandidat) === null;
    }

    private static harLikeAlternativer(a: SpørsmålSkjema, b: SpørsmålSkjema): boolean {
        const nøkkel = (spørsmål: SpørsmålSkjema) => JSON.stringify(spørsmål.controls.alternativer.getRawValue()
          .map(alternativ => [alternativ.tittel, alternativ.verdi, alternativ.farge.toLowerCase()]));
        return nøkkel(a) === nøkkel(b);
    }
}
